import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Lock, Mail, User, Eye, EyeOff } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { adminLogin, adminRegister } from '@/lib/api';

const AdminLoginPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loginData, setLoginData] = useState({ email: '', password: '' });
  const [registerData, setRegisterData] = useState({ email: '', password: '', name: '' });

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginData.email || !loginData.password) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setLoading(true);
    try {
      const response = await adminLogin(loginData);
      localStorage.setItem('admin_token', response.data.token);
      toast.success('Connexion réussie !');
      navigate('/admin/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur de connexion';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!registerData.email || !registerData.password || !registerData.name) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setLoading(true);
    try {
      const response = await adminRegister(registerData);
      localStorage.setItem('admin_token', response.data.token);
      toast.success('Compte créé avec succès !');
      navigate('/admin/dashboard');
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la création';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bone flex items-center justify-center p-6">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Heart className="w-8 h-8 text-terracotta" fill="#D4A373" />
          </div>
          <h1 className="font-display text-3xl text-charcoal mb-2" data-testid="admin-login-title">
            Espace Administrateur
          </h1>
          <p className="text-stone text-sm">Mariage Mélanie & Cédric</p>
        </div>

        {/* Tabs */}
        <div className="card-elegant">
          <Tabs defaultValue="login" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger value="login" data-testid="tab-login">Connexion</TabsTrigger>
              <TabsTrigger value="register" data-testid="tab-register">Créer un compte</TabsTrigger>
            </TabsList>

            {/* Login Form */}
            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label htmlFor="login-email" className="text-charcoal font-medium flex items-center gap-2">
                    <Mail className="w-4 h-4 text-terracotta" />
                    Email
                  </Label>
                  <Input
                    id="login-email"
                    data-testid="input-login-email"
                    type="email"
                    value={loginData.email}
                    onChange={(e) => setLoginData((prev) => ({ ...prev, email: e.target.value }))}
                    placeholder="admin@exemple.com"
                    className="input-elegant mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="login-password" className="text-charcoal font-medium flex items-center gap-2">
                    <Lock className="w-4 h-4 text-terracotta" />
                    Mot de passe
                  </Label>
                  <div className="relative mt-2">
                    <Input
                      id="login-password"
                      data-testid="input-login-password"
                      type={showPassword ? 'text' : 'password'}
                      value={loginData.password}
                      onChange={(e) => setLoginData((prev) => ({ ...prev, password: e.target.value }))}
                      placeholder="••••••••"
                      className="input-elegant pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-stone hover:text-charcoal"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  data-testid="btn-login"
                  disabled={loading}
                  className="btn-primary w-full mt-6"
                >
                  {loading ? 'Connexion...' : 'Se connecter'}
                </Button>
              </form>
            </TabsContent>

            {/* Register Form */}
            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <Label htmlFor="register-name" className="text-charcoal font-medium flex items-center gap-2">
                    <User className="w-4 h-4 text-terracotta" />
                    Nom
                  </Label>
                  <Input
                    id="register-name"
                    data-testid="input-register-name"
                    type="text"
                    value={registerData.name}
                    onChange={(e) => setRegisterData((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder="Mélanie & Cédric"
                    className="input-elegant mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="register-email" className="text-charcoal font-medium flex items-center gap-2">
                    <Mail className="w-4 h-4 text-terracotta" />
                    Email
                  </Label>
                  <Input
                    id="register-email"
                    data-testid="input-register-email"
                    type="email"
                    value={registerData.email}
                    onChange={(e) => setRegisterData((prev) => ({ ...prev, email: e.target.value }))}
                    placeholder="admin@exemple.com"
                    className="input-elegant mt-2"
                  />
                </div>
                <div>
                  <Label htmlFor="register-password" className="text-charcoal font-medium flex items-center gap-2">
                    <Lock className="w-4 h-4 text-terracotta" />
                    Mot de passe
                  </Label>
                  <div className="relative mt-2">
                    <Input
                      id="register-password"
                      data-testid="input-register-password"
                      type={showPassword ? 'text' : 'password'}
                      value={registerData.password}
                      onChange={(e) => setRegisterData((prev) => ({ ...prev, password: e.target.value }))}
                      placeholder="••••••••"
                      className="input-elegant pr-10"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-stone hover:text-charcoal"
                    >
                      {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </button>
                  </div>
                </div>
                <Button
                  type="submit"
                  data-testid="btn-register"
                  disabled={loading}
                  className="btn-primary w-full mt-6"
                >
                  {loading ? 'Création...' : 'Créer le compte'}
                </Button>
              </form>
              <p className="text-xs text-stone text-center mt-4">
                Note : Un seul compte administrateur peut être créé.
              </p>
            </TabsContent>
          </Tabs>
        </div>

        {/* Back Link */}
        <div className="text-center mt-6">
          <a href="/" className="text-sm text-stone hover:text-terracotta transition-colors">
            ← Retour au site
          </a>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminLoginPage;
